<template>
  <div id="top" class="provider-onboard-page">
    <div class="provider-onboard-intro">
      <UiPageHeader title="Add Provider" description="Capture provider details, choose an operating plan, and optionally upload a custom tariff.">
        <template #actions><router-link :to="{ name: 'provider' }" class="provider-onboard-back"><i class="material-icons" aria-hidden="true">arrow_back</i>All providers</router-link></template>
      </UiPageHeader>
    </div>
    <div class="provider-add-workbench">
      <LoadingDiv v-if="!isReady" />

      <section class="provider-add-body" v-else>
        <form @submit.prevent="handleSubmit" class="provider-add-form">
          <section class="provider-add-card">
            <div class="provider-add-section-head">
              <div>
                <h2>Provider details</h2>
                <p>Enter the provider’s identity and contact information.</p>
              </div>
            </div>

            <div class="provider-add-grid">
              <div class="provider-add-field">
                <div class="input-field">
                  <input id="name" type="text" v-model="name" class="validate" required />
                  <label for="name">Provider Name</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <input id="md" type="text" v-model="md" class="validate" required />
                  <label for="md">MD's Name</label>
                </div>
              </div>

              <div class="provider-add-field is-wide">
                <div class="input-field">
                  <input id="address" type="text" v-model="address" class="validate" required />
                  <label for="address">Provider Address</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <input id="phone" type="text" v-model="phone" class="validate" required />
                  <label for="phone">Provider Phone</label>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field">
                  <label for="email">Provider Email</label>
                  <div class="provider-email-control">
                  <input
                    id="email"
                    name="provider-add-email"
                    type="email"
                    v-model="email"
                    class="validate"
                    autocomplete="new-password"
                    autocapitalize="none"
                    autocorrect="off"
                    spellcheck="false"
                    required
                  />

                  <div class="progress provider-add-inline-progress" v-if="isChecking">
                    <div class="indeterminate"></div>
                  </div>
                  </div>
                  <p class="provider-add-checking" v-if="email && isChecking">
                    Checking email availability...
                  </p>
                  <p class="provider-add-warning" v-else-if="email && !isChecking && checkedEmailCounter > 0">
                    Sorry, email already belongs to another provider.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section class="provider-add-card">
            <div class="provider-add-section-head">
              <div>
                <h2>Plan and tariff</h2>
                <p>Choose an operating plan and upload a custom tariff if needed.</p>
              </div>
            </div>

            <div class="provider-add-grid">
              <div class="provider-add-field">
                <div class="input-field">
                  <label for="provider-plan">Plan</label>
                  <select id="provider-plan" v-model="plan">
                    <option value="" disabled selected>Select HMO Plan</option>
                    <option v-for="plan in plans" :key="plan.id" :value="plan.id">
                      {{ plan.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="provider-add-field">
                <div class="input-field provider-add-file-field">
                  <label for="provider-tariff">Custom tariff (optional)</label>
                  <input id="provider-tariff" type="file" @change="handleFile" />
                </div>
                <p class="provider-add-warning" v-if="fileError">{{ fileError }}</p>
              </div>
            </div>
          </section>

          <p class="provider-add-warning is-block" v-if="uploadError">{{ uploadError }}</p>

          <section class="provider-add-action-card">
            <div>
              <span>Create provider account</span>
              <small>The provider will be assigned to active provider modules and schemes after submission.</small>
            </div>
            <span class="provider-add-error" v-if="error">{{ error }}</span>
            <button
              type="submit"
              :disabled="!name || !md || !address || !phone || !email || !plan || !allModules?.length || !allSchemes?.length || checkedEmailCounter > 0 || isChecking || isSubmitting"
              class="waves-effect waves-light btn-large provider-add-submit"
              :class="{
                disabled:
                  !name ||
                  !md ||
                  !address ||
                  !phone ||
                  !email ||
                  !plan ||
                  !allModules?.length ||
                  !allSchemes?.length ||
                  checkedEmailCounter > 0 ||
                  isChecking ||
                  isSubmitting,
              }"
            >
              <i class="material-icons left" v-if="!isSubmitting">check_circle</i>
              {{ isSubmitting ? `Submitting (${uploadProgress})` : "Add Provider" }}
            </button>
          </section>
        </form>
      </section>
    </div>
  </div>
</template>


<script>
import { ref, onMounted, onUpdated, computed, watchEffect } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import useModuleNavigation from "@/composables/useModuleNavigation";
import useStorage from "@/composables/useStorage";
import getItems from "@/composables/getItems";
import postItem from "@/composables/postItem";
import {
  debounce,
  getCachedPayload,
  hasBasicEmailShape,
  setCachedPayload,
} from "@/composables/requestOptimization";
import UiPageHeader from "@/ui/components/UiPageHeader.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { addModules } from "@/composables/cache/modules";
import { adminAPI } from '@/laravel/config';
import nativeUi from "@/ui/native-ui";


export default {
  name: "providerAdd",
  components: { UiPageHeader, LoadingDiv },
  setup() {
    const store = useStore();
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const response = ref(null);
    const isReady = ref(false);
    const isSubmitting = ref(false);
    const isChecking = ref(false);
    const error = ref(null);
    const name = ref(null);
    const md = ref(null);
    const address = ref(null);
    const phone = ref(null);
    const email = ref(null);
    const checkedEmailCounter = ref(1); // default to 1 to disable next button
    const plan = ref(null);
    const allModules = ref([]);
    const allSchemes = ref([]);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();
    const { routeToTab } = useModuleNavigation();

    // Start
    onMounted(() => {
      // page name
      document.title = "Add Provider";
    });

    // Modules and Schemes
    onMounted(() => {
      // get modules
      allModules.value = addModules()
        .filter(x => x.type === "provider")
        .map(item => item.to);

      // get schemes
      (async () => {
        const { res } = await getItems("scheme");

        if (res.value) {
          allSchemes.value = res.value.data
            .map(item => item.id);
        } else {
          error.value = "ERROR: Important data not loaded!";
        }

      })();
    });

    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "All", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: null, url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: "sync", url: "providerSync", hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Add"));
      // set home
      home.value = tabs.value.find(x => x.id === 1);
    });

    // handle curr status
    const handleActive = (tab) => {
      routeToTab(tab, { replace: tab?.url === "providerAdd" });
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = "ERROR: Plans are not loaded!";
        }

        // page is ready
        isReady.value = true;
      })();
    };

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // starter
    onMounted(() => {
      // get plans
      getItemsFromApi("plan/private");
    });

    // drop down
    onUpdated(() => {
      // activate select field
      const elemsSelect = document.querySelectorAll("select");
      nativeUi.FormSelect.init(elemsSelect, {});
    });

    // clear
    const clearForm = () => {
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      isSubmitting.value = false;
      uploadProgress.value = "0%";
    };

    const normalizedEmail = computed(() => {
      return email.value ? email.value.trim().toLowerCase() : null;
    });
    const emailCheckCacheTtl = 60 * 1000;
    let emailCheckRequestId = 0;

    const runEmailAvailabilityCheck = debounce(async (currentEmail, requestId) => {
      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      const cacheKey = `provider-email-check:${currentEmail}`;
      const cachedCount = getCachedPayload(cacheKey);

      if (cachedCount !== null) {
        checkedEmailCounter.value = cachedCount;
        isChecking.value = false;
        return;
      }

      const { res } = await getItems(`provider/email/checker?email=${encodeURIComponent(currentEmail)}`);

      if (requestId !== emailCheckRequestId || currentEmail !== normalizedEmail.value) {
        return;
      }

      if (res.value) {
        checkedEmailCounter.value = res.value.data;
        setCachedPayload(cacheKey, res.value.data, emailCheckCacheTtl);
      } else {
        checkedEmailCounter.value = 1;
      }

      isChecking.value = false;
    });

    // file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
      }
    };

    const handleSubmit = async () => {
      if (isChecking.value || checkedEmailCounter.value > 0) {
        return;
      }

      // Start Uploading
      isSubmitting.value = true;

      // sort default Schemes and Modules
      let schemes = allSchemes.value;
      let modules = allModules.value;
      let providerEmail = normalizedEmail.value;

      // check file or no file creation
      if (file.value) {
        uploadError.value = null;

        // Cloud Function
        const fx = "tariffLoader";

        // Start
        let schedule = {
          plan_id: plan.value,
          type: "c", // c=customized, s=standard
        };

        // Continue
        let newProvider = {
          schedule_id: null,
          name: name.value,
          md: md.value,
          email: providerEmail,
          phone: phone.value,
          address: address.value,
        };

        // Round up
        const cloudInput = {
          schedule: schedule,
          schemes: schemes,
          modules: modules,
          provider: null,
          newProvider: newProvider,
          endPoint: `${adminAPI}/schedule/tariffs/bulk`,
          authToken: store.state?.user?.token || null,
        };

        // upload
        uploadFile(file.value, fx, cloudInput);
      } else {
        // Submit Single
        uploadProgress.value = "100%"; // so its not blank
        // start
        let newProvider = {
          schedule_id: null,
          name: name.value,
          md: md.value,
          email: providerEmail,
          phone: phone.value,
          address: address.value,
        };

        // round up
        const newVal = {
          schemes: schemes,
          modules: modules,
          plan: plan.value, // server will find applicable standard schedule
          provider: newProvider,
        };

        // post item
        const { res, err } = await postItem("provider/add", newVal);

        if (res.value) {
          // final
          router.push({
            name: "providerView",
            params: { id: res.value.data.id },
          });

          // clear all
          clearForm();
        } else {
          error.value = err.value;
          isSubmitting.value = false;
        }
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        //do stuff...
        // data.succ.Success, data.succ.inserted_rows, data.succ.schedule, data.succ.provider, data.succ.Error

        router.push({
          name: "providerView",
          params: { id: data.value.succ.provider.id },
        });

        // clear all
        clearForm();
      }
      if (uploadError.value) {
        // clear all
        clearForm();
      }
    });

    // watch upload
    watchEffect(() => {
      const currentEmail = normalizedEmail.value;

      if (!currentEmail) {
        checkedEmailCounter.value = 1;
        isChecking.value = false;
        emailCheckRequestId++;
        return;
      }

      if (!hasBasicEmailShape(currentEmail)) {
        checkedEmailCounter.value = 1;
        isChecking.value = false;
        emailCheckRequestId++;
        return;
      }

      // search email
      isChecking.value = true;
      checkedEmailCounter.value = 0;
      emailCheckRequestId++;
      runEmailAvailabilityCheck(currentEmail, emailCheckRequestId);
    });

    // output results
    const plans = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      handleActive,
      home,
      currTab,
      tabs,
      handleFile,
      handleSubmit,
      allModules,
      allSchemes,
      isReady,
      error,
      name,
      md,
      address,
      phone,
      email,
      checkedEmailCounter,
      isSubmitting,
      isChecking,
      plans,
      plan,
      fileError,
      uploadError,
      file,
      uploadProgress,
      inputFile,
    };
  },
};
</script>

<style scoped>
.provider-onboard-page { box-sizing: border-box; margin: 0 auto; max-width: var(--ui-content-width); padding: 2rem 2rem 3rem; width: 100%; }
.provider-onboard-intro { border-bottom: 1px solid var(--ui-border); margin-bottom: 2rem; padding-bottom: 1.75rem; }
.provider-onboard-intro :deep(.ui-page-header) { margin-bottom: 0; }
.provider-onboard-intro :deep(.ui-page-header__title) { font-size: clamp(1.75rem, 2.1vw, 2rem); font-weight: 700; letter-spacing: -.025em; }
.provider-onboard-intro :deep(.ui-page-header__description) { font-size: 1rem; line-height: 1.55; }
.provider-onboard-back { align-items: center; background: var(--ui-primary); border: 1px solid var(--ui-primary); border-radius: calc(var(--ui-radius) - 2px); color: var(--ui-primary-foreground); display: inline-flex; font-size: .8125rem; font-weight: 600; gap: .5rem; min-height: 2.5rem; padding: 0 .9rem; text-decoration: none; }
.provider-onboard-back i { font-size: 1rem; }
.provider-onboard-page .provider-add-section-head h2 { color: var(--ui-foreground); font-size: 1rem; font-weight: 600; line-height: 1.5; margin: 0 0 .35rem; }
.provider-onboard-page .provider-add-section-head p { color: var(--ui-muted-foreground); font-size: .8125rem; line-height: 1.6; margin: 0; }
.provider-email-control { position: relative; }
.provider-onboard-page :is(a, button):focus-visible { outline: 2px solid var(--ui-ring); outline-offset: 2px; }
@layer components {
  .provider-onboard-page .provider-add-body { background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; padding: 0 !important; }
  .provider-onboard-page .provider-add-form { gap: 0 !important; max-width: 70rem; width: 100%; }
  .provider-onboard-page .provider-add-card { align-items: start; background: transparent !important; border: 0 !important; border-bottom: 1px solid var(--ui-border) !important; border-radius: 0 !important; box-shadow: none !important; display: grid; gap: 2.5rem; grid-template-columns: 13rem minmax(0, 1fr); padding: 1.75rem 0; }
  .provider-onboard-page .provider-add-card:first-child { padding-top: .5rem; }
  .provider-onboard-page .provider-add-section-head { border: 0 !important; padding: 0 !important; }
  .provider-onboard-page .provider-add-grid { gap: 1.25rem !important; padding: 0 !important; }
  .provider-onboard-page .provider-add-field { background: transparent !important; border: 0 !important; border-radius: 0 !important; min-width: 0; padding: 0 !important; }
  .provider-onboard-page .provider-add-field .input-field { display: flex !important; flex-direction: column !important; margin: 0 !important; }
  .provider-onboard-page .provider-add-field .input-field > label { color: var(--ui-foreground) !important; font-size: .8125rem !important; font-weight: 500 !important; line-height: 1.5; margin: 0 0 .5rem !important; order: -1; position: static !important; transform: none !important; }
  .provider-onboard-page .provider-add-field input, .provider-onboard-page .provider-add-field select { box-sizing: border-box; display: block; margin: 0 !important; min-height: 2.75rem; width: 100%; }
  .provider-onboard-page .provider-add-inline-progress { bottom: 1px !important; left: 1px !important; right: 1px !important; top: auto !important; width: auto !important; }
  .provider-onboard-page .provider-add-action-card { align-items: center; background: transparent !important; border: 0 !important; border-radius: 0 !important; box-shadow: none !important; display: flex; flex-wrap: wrap; gap: 1rem; padding: 1.25rem 0 0 !important; }
  .provider-onboard-page .provider-add-action-card > div { flex: 1 1 20rem; }
  .provider-onboard-page .provider-add-action-card > div > span { font-size: .875rem !important; font-weight: 600 !important; }
  .provider-onboard-page .provider-add-action-card small { color: var(--ui-muted-foreground); font-size: .75rem !important; line-height: 1.6; }
  .provider-onboard-page .provider-add-submit { box-shadow: none !important; font-size: .8125rem !important; font-weight: 600 !important; gap: .5rem; min-height: 2.5rem !important; padding: 0 1rem !important; width: auto !important; }
  .provider-onboard-page .provider-add-submit i { font-size: 1rem !important; margin: 0 !important; }
  @media (max-width: 900px) { .provider-onboard-page .provider-add-card { gap: 1rem; grid-template-columns: minmax(0, 1fr); } }
  @media (max-width: 600px) { .provider-onboard-page .provider-add-grid { grid-template-columns: minmax(0, 1fr); } }
}
@media (max-width: 700px) { .provider-onboard-page { padding: 1.25rem 1rem 2rem; } }
.provider-add-breadcrumb {
  background: #fafafa !important;
  position: relative;
}

.provider-add-breadcrumb::after {
  background: #c4d3d9;
  bottom: 0;
  content: "";
  height: 1px;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
}

.provider-add-page-shell {
  margin-top: -12px;
  padding-bottom: 44px;
  padding-top: 0;
  position: relative;
  z-index: 1;
}

.provider-add-workbench {
  color: #263238;
}

.provider-add-head,
.provider-add-body,
.provider-add-card,
.provider-add-action-card,
.provider-add-field {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.provider-add-head {
  background: #ffffff;
  border: 1px solid #c4d3d9;
  border-radius: 6px 6px 0 0;
  box-shadow: 0 12px 20px -18px rgba(38, 50, 56, 0.55);
  margin-bottom: 0;
  padding: 24px 26px;
  position: relative;
  z-index: 2;
}

.provider-add-head::before {
  border: 1px solid #c4d3d9;
  border-bottom: 0;
  border-radius: 7px 7px 0 0;
  content: "";
  height: 18px;
  left: -1px;
  pointer-events: none;
  position: absolute;
  right: -1px;
  top: -1px;
  z-index: 1;
}

.provider-add-title-block {
  position: relative;
  z-index: 2;
}

.provider-add-title-block h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.provider-add-title-block p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
  max-width: 780px;
}

.provider-add-body {
  background: #f7f9f9;
  border: 1px solid #c4d3d9;
  border-radius: 0 0 6px 6px;
  border-top: 0;
  box-shadow: 0 18px 34px -31px rgba(38, 50, 56, 0.75);
  padding: 22px 18px 18px;
  position: relative;
  z-index: 1;
}

.provider-add-form {
  display: grid;
  gap: 16px;
  margin: 0;
}

.provider-add-card,
.provider-add-action-card {
  background: #ffffff;
  border: 1px solid #dde6e9;
  border-radius: 6px;
  box-shadow: 0 18px 32px -30px rgba(38, 50, 56, 0.72);
  overflow: visible;
}

.provider-add-section-head {
  align-items: center;
  border-bottom: 1px solid #edf2f3;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 22px 14px;
}

.provider-add-section-head label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 7px;
}

.provider-add-section-head h6 {
  color: #263238;
  font-size: 1.08rem;
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
}

.provider-add-section-head span {
  background: #e8f5f3;
  border: 1px solid #b8dfda;
  border-radius: 999px;
  color: #00695c;
  flex: 0 0 auto;
  font-size: 0.74rem;
  font-weight: 800;
  padding: 6px 10px;
}

.provider-add-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  padding: 18px 20px 20px;
}

.provider-add-field {
  background: #fbfcfc;
  border: 1px solid #e3eaed;
  border-radius: 6px;
  padding: 16px 18px 10px;
}

.provider-add-field.is-wide {
  grid-column: 1 / -1;
}

.provider-add-field :deep(.input-field),
.provider-add-file-field {
  margin-bottom: 0.45rem;
  margin-top: 0.35rem;
}

.provider-add-field :deep(input:not([type])),
.provider-add-field :deep(input[type=text]:not(.browser-default)),
.provider-add-field :deep(input[type=email]:not(.browser-default)),
.provider-add-field :deep(input[type=number]:not(.browser-default)) {
  color: #263238;
  margin-bottom: 0;
}

.provider-add-field :deep(input:not([type]):focus:not([readonly])),
.provider-add-field :deep(input[type=text]:not(.browser-default):focus:not([readonly])),
.provider-add-field :deep(input[type=email]:not(.browser-default):focus:not([readonly])),
.provider-add-field :deep(input[type=number]:not(.browser-default):focus:not([readonly])) {
  border-bottom-color: #00897b;
  box-shadow: 0 1px 0 0 #00897b;
}

.provider-add-file-field .btn {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  text-transform: none;
}

.provider-add-field .input-field input:-webkit-autofill,
.provider-add-field .input-field input:-webkit-autofill:hover,
.provider-add-field .input-field input:-webkit-autofill:focus {
  -webkit-text-fill-color: #263238;
  box-shadow: 0 0 0 1000px #fbfcfc inset;
  transition: background-color 9999s ease-out 0s;
}

.provider-add-inline-progress {
  background-color: #e8f3f2;
  border-radius: 0;
  height: 3px;
  left: 0;
  margin: 0;
  position: absolute;
  right: 0;
  top: 44px;
  width: 100%;
  z-index: 2;
}

.provider-add-inline-progress .indeterminate {
  background-color: #00897b;
}

.provider-add-checking {
  color: #78909c;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.25;
  margin: 10px 0 0;
}

.provider-add-warning,
.provider-add-error {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 999px;
  color: #b71c1c;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 10px 0 0;
  padding: 7px 10px;
}

.provider-add-warning.is-block {
  border-radius: 6px;
  margin: 0;
  max-width: max-content;
}

.provider-add-action-card {
  align-items: center;
  background: #ffffff;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 18px 20px;
}

.provider-add-action-card span:not(.provider-add-error) {
  color: #263238;
  display: block;
  font-weight: 700;
}

.provider-add-action-card small {
  color: #607d8b;
  display: block;
  line-height: 1.45;
  margin-top: 4px;
}

.provider-add-submit {
  background: #00897b;
  border-radius: 5px;
  box-shadow: 0 10px 18px -16px rgba(38, 50, 56, 0.8);
  flex: 0 0 auto;
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.provider-add-submit:hover {
  background: #00695c;
}

.pointer {
  cursor: default;
}

@media only screen and (max-width: 800px) {
  .provider-add-grid {
    grid-template-columns: 1fr;
  }

  .provider-add-action-card {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media only screen and (max-width: 600px) {
  .provider-add-page-shell {
    width: 94%;
  }

  .provider-add-head {
    padding: 20px;
  }

  .provider-add-title-block h5 {
    font-size: 1.45rem;
  }

  .provider-add-title-block p {
    font-size: 0.95rem;
  }

  .provider-add-body {
    padding: 18px 12px;
  }

  .provider-add-grid {
    padding-left: 14px;
    padding-right: 14px;
  }

  .provider-add-section-head,
  .provider-add-action-card {
    align-items: flex-start;
    flex-direction: column;
  }

  .provider-add-section-head span,
  .provider-add-submit,
  .provider-add-file-field .btn,
  .provider-add-warning,
  .provider-add-error {
    justify-content: center;
    width: 100%;
  }

  .provider-add-field {
    padding: 14px 14px 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .provider-add-head,
  .provider-add-body,
  .provider-add-card,
  .provider-add-action-card,
  .provider-add-field {
    animation: none;
  }
}
</style>
